import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Scan,
  Globe,
  Zap,
  ArrowRight,
  ScanEye,
  Newspaper,
  Megaphone,
  GraduationCap,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { isMaintenanceMode } from "@/utils/maintenanceMode";

const API = `${process.env.REACT_APP_API_URL}/api`;

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Home() {
  const [stats, setStats] = useState({ total: 0, fake: 0, real: 0 });
  const [freePlan, setFreePlan] = useState(null);
  const [starterPlan, setStarterPlan] = useState(null);
  const { t, i18n } = useTranslation();
  const maintenanceMode = isMaintenanceMode();

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios
      .get(`${API}/subscription/plans`)
      .then((r) => {
        const plans = Array.isArray(r.data) ? r.data : [];
        const activePlans = plans.filter((plan) => plan?.is_active !== false);
        const free = activePlans.find((plan) => String(plan?.id || "").toLowerCase() === "free") || null;
        const paidPublic = activePlans
          .filter(
            (plan) =>
              String(plan?.id || "").toLowerCase() !== "free" &&
              plan?.is_public !== false &&
              Number(plan?.price_idr || 0) > 0,
          )
          .sort((a, b) => {
            const priceDiff = Number(a?.price_idr || 0) - Number(b?.price_idr || 0);
            if (priceDiff !== 0) return priceDiff;
            return Number(a?.sort_order || 0) - Number(b?.sort_order || 0);
          });
        setFreePlan(free);
        setStarterPlan(paidPublic[0] || null);
      })
      .catch(() => {});
  }, []);

  const features = [
    { icon: Scan,       titleKey: "home.features.advanced.title", descKey: "home.features.advanced.desc" },
    { icon: Globe,      titleKey: "home.features.url.title",      descKey: "home.features.url.desc" },
    { icon: Zap,        titleKey: "home.features.fast.title",     descKey: "home.features.fast.desc" },
    { icon: ShieldCheck,titleKey: "home.features.secure.title",   descKey: "home.features.secure.desc" },
  ];
  const useCases = [
    { icon: Newspaper, titleKey: "home.useCases.journalist.title", descKey: "home.useCases.journalist.desc" },
    { icon: Megaphone, titleKey: "home.useCases.comms.title", descKey: "home.useCases.comms.desc" },
    { icon: GraduationCap, titleKey: "home.useCases.research.title", descKey: "home.useCases.research.desc" },
    { icon: Briefcase, titleKey: "home.useCases.enterprise.title", descKey: "home.useCases.enterprise.desc" },
  ];
  const locale = i18n.language?.startsWith("id") ? "id-ID" : "en-US";
  const totalText = new Intl.NumberFormat(locale).format(stats.total);
  const formatIdr = (amount) => `Rp ${new Intl.NumberFormat(locale).format(Number(amount || 0))}`;
  const freeLimit = Number(freePlan?.limit || 5);
  const starterLimit = Number(starterPlan?.limit || 30);
  const starterPrice = Number(starterPlan?.price_idr || 79000);
  const starterId = String(starterPlan?.id || "starter").toLowerCase();
  const starterName = t(`pricing.plan.${starterId}`, {
    defaultValue: starterPlan?.name || t("pricing.plan.starter"),
  });

  return (
    <main className="" data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[calc(100vh-72px)] flex items-center overflow-hidden">
        {/* BG texture */}
        <div
          className="absolute inset-0 opacity-[0.04] bg-center bg-cover"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1704392451700-42e34133a883?w=1400&q=80)" }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 grid md:grid-cols-2 gap-12 items-center w-full">
          {/* Left: Typography */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-6"
          >
            <motion.div variants={item}>
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground border border-border rounded-sm px-3 py-1.5">
                {t("home.badge")}
              </span>
            </motion.div>
            <motion.h1
              variants={item}
              className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-none"
              data-testid="hero-heading"
            >
              {t("home.hero1")}{" "}
              <span className="text-brand-primary">{t("home.hero2")}</span>
            </motion.h1>
            <motion.p
              variants={item}
              className="text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed"
            >
              {t("home.heroDesc")}
            </motion.p>
            {!maintenanceMode && (
              <motion.div variants={item} className="flex flex-wrap gap-3 pt-2">
                <Link to="/analyze">
                  <Button
                    size="lg"
                    className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white font-medium gap-2"
                    data-testid="hero-cta-analyze"
                  >
                    {t("home.cta.analyze")} <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="#features-section">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-md font-medium"
                    data-testid="hero-cta-history"
                  >
                    {t("home.cta.demo")}
                  </Button>
                </a>
              </motion.div>
            )}

            {/* Stats */}
            {stats.total > 0 && (
              <motion.div variants={item} className="flex gap-8 pt-4 border-t border-border">
                <div>
                  <div className="text-2xl font-heading font-bold" data-testid="stat-total">{totalText}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("home.stats.analyzed")}</div>
                </div>
                {stats.total >= 100 && (
                  <>
                    <div>
                      <div className="text-2xl font-heading font-bold text-verdict-fake" data-testid="stat-fake">{stats.fake}</div>
                      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("home.stats.highRisk")}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-heading font-bold text-verdict-real" data-testid="stat-real">{stats.real}</div>
                      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("home.stats.lowRisk")}</div>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </motion.div>

          {/* Right: Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden md:flex items-center justify-center"
          >
            <div className="relative w-full max-w-md aspect-video rounded-md border border-border bg-card overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1765445665883-085301570c87?w=800&q=80"
                alt="AI analysis visualization"
                className="w-full h-full object-cover opacity-30"
              />
              {/* Scan overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <div className="w-16 h-16 rounded-sm bg-brand-primary/10 border border-brand-primary/30 flex items-center justify-center">
                  <ScanEye className="w-8 h-8 text-brand-primary" />
                </div>
                <div className="text-center">
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                    {t("home.analysisEngine", "Risk Analysis Engine")}
                  </div>
                  <div className="font-heading font-semibold text-lg">{t("home.heroTitle", "Analisis Risiko Media")}</div>
                </div>
              </div>
              {/* Scanlines */}
              <div
                className="absolute inset-0 pointer-events-none opacity-10"
                style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.05) 2px, rgba(255,255,255,0.05) 4px)" }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features-section" className="scroll-mt-24 py-20 border-t border-border" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-12">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              {t("home.features.eyebrow")}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mt-2">
              {t("home.howItWorks")}
            </h2>
          </div>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {features.map(({ icon: Icon, titleKey, descKey }) => (
              <motion.div
                key={titleKey}
                variants={item}
                className="p-6 rounded-md border border-border bg-card hover:border-brand-primary/30 transition-colors"
                data-testid={`feature-card-${titleKey}`}
              >
                <div className="w-10 h-10 rounded-sm bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-brand-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{t(titleKey)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 border-t border-border" data-testid="use-cases-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-12 text-center md:text-left">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              {t("home.useCases.eyebrow")}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mt-2">
              {t("home.useCases.title")}
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              {t("home.useCases.desc")}
            </p>
          </div>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {useCases.map(({ icon: Icon, titleKey, descKey }) => (
              <motion.div
                key={titleKey}
                variants={item}
                className="p-6 rounded-md border border-border bg-card hover:border-brand-primary/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-sm bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-brand-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{t(titleKey)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {!maintenanceMode && (
        <section id="pricing-section" className="scroll-mt-24 py-20 border-t border-border" data-testid="pricing-section">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("nav.pricing")}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mt-2">{t("home.pricing.title")}</h2>
              <p className="text-muted-foreground mt-3 text-sm max-w-xl mx-auto">{t("home.pricing.desc")}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-md border border-border bg-card p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-sm border border-border bg-secondary/20 p-4">
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{t("pricing.free")}</p>
                  <p className="font-heading text-2xl font-bold">Rp 0</p>
                  <p className="text-xs text-muted-foreground mt-1">{freeLimit} {t("pricing.analyses")}</p>
                </div>
                <div className="rounded-sm border border-brand-primary/30 bg-brand-primary/5 p-4">
                  <p className="text-xs font-mono uppercase tracking-widest text-brand-primary mb-2">{starterName}</p>
                  <p className="font-heading text-2xl font-bold text-brand-primary">{formatIdr(starterPrice)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{starterLimit} {t("pricing.analyses")}</p>
                </div>
                <div className="flex flex-col justify-between gap-2">
                  <p className="text-sm text-muted-foreground">{t("pricing.subtitle")}</p>
                  <div className="flex gap-2">
                    <Link to="/pricing" className="flex-1">
                      <Button variant="outline" className="w-full rounded-sm">{t("nav.pricing")}</Button>
                    </Link>
                    <Link to="/register" className="flex-1">
                      <Button className="w-full rounded-sm bg-brand-primary hover:bg-brand-primary/90 text-white">{t("nav.getStarted")}</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Bottom */}
      <section className="py-20 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              {t("home.cta.title")}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              {t("home.cta.desc")}
            </p>
            {!maintenanceMode && (
              <Link to="/analyze">
                <Button
                  size="lg"
                  className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white font-medium gap-2"
                  data-testid="bottom-cta-btn"
                >
                  {t("home.cta.start")} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
          </motion.div>
        </div>
      </section>

    </main>
  );
}
